import type {
  BookCondition,
  DateOperator,
  FieldOperator,
  NumberOperator,
  SeriesCondition,
  UuidOperator,
} from "@/types/filters";
import {
  type FieldDef,
  type FieldTarget,
  findField,
  findFieldAnyTarget,
  type OperatorType,
} from "./fieldCatalog";

/**
 * The condition tree the builder works with. Mirrors the API grammar
 * exactly so we don't have to convert back and forth.
 */
export type Condition = SeriesCondition | BookCondition;

/** A combinator node: `{ allOf: ... }` or `{ anyOf: ... }`. */
export interface GroupNode {
  mode: "allOf" | "anyOf";
  children: Condition[];
}

export function isGroup(
  c: Condition,
): c is Extract<Condition, { allOf: unknown } | { anyOf: unknown }> {
  return typeof c === "object" && c !== null && ("allOf" in c || "anyOf" in c);
}

export function asGroup(c: Condition): GroupNode | null {
  if ("allOf" in c && Array.isArray((c as { allOf: unknown[] }).allOf)) {
    return {
      mode: "allOf",
      children: (c as { allOf: Condition[] }).allOf,
    };
  }
  if ("anyOf" in c && Array.isArray((c as { anyOf: unknown[] }).anyOf)) {
    return {
      mode: "anyOf",
      children: (c as { anyOf: Condition[] }).anyOf,
    };
  }
  return null;
}

export function makeGroup(group: GroupNode): Condition {
  return group.mode === "allOf"
    ? ({ allOf: group.children } as Condition)
    : ({ anyOf: group.children } as Condition);
}

/**
 * Get the field key from a leaf condition, or null if the condition is a
 * group / malformed.
 */
export function leafFieldKey(c: Condition): string | null {
  if (isGroup(c)) return null;
  const keys = Object.keys(c);
  return keys.length === 1 ? keys[0] : null;
}

export function leafOperator(c: Condition): string | null {
  const key = leafFieldKey(c);
  if (!key) return null;
  const op = (c as Record<string, { operator?: string }>)[key];
  return op?.operator ?? null;
}

/**
 * Operator → display label, per operator family. Used to populate the
 * Operator dropdown in the LeafEditor.
 */
export const OPERATOR_LABELS: Record<OperatorType, Record<string, string>> = {
  field: {
    is: "is",
    isNot: "is not",
    contains: "contains",
    doesNotContain: "does not contain",
    beginsWith: "begins with",
    endsWith: "ends with",
    isNull: "is empty",
    isNotNull: "is not empty",
  },
  uuid: {
    is: "is",
    isNot: "is not",
    in: "is any of",
    notIn: "is none of",
  },
  bool: {
    isTrue: "is true",
    isFalse: "is false",
  },
  number: {
    eq: "equals",
    ne: "is not",
    gt: "greater than",
    gte: "≥",
    lt: "less than",
    lte: "≤",
    between: "between",
    isNull: "is empty",
    isNotNull: "is not empty",
  },
  date: {
    after: "after",
    before: "before",
    onOrAfter: "on or after",
    onOrBefore: "on or before",
    between: "between",
    isNull: "is empty",
    isNotNull: "is not empty",
  },
};

/**
 * Operators legal for a given field. For "field"-type fields that carry
 * a closed enum (e.g. `format`, `bookType`), strip operators that take a
 * free-form value — only equality + nullability survive.
 */
export function operatorsForField(field: FieldDef): string[] {
  const all = Object.keys(OPERATOR_LABELS[field.operatorType]);
  if (field.operatorType === "field" && field.enumValues) {
    return all.filter(
      (op) =>
        op === "is" || op === "isNot" || op === "isNull" || op === "isNotNull",
    );
  }
  return all;
}

/**
 * Default operator for a fresh field. Picks the most natural starting point
 * given the operator family.
 */
export function defaultOperator(field: FieldDef): string {
  switch (field.operatorType) {
    case "field":
      return field.enumValues ? "is" : "contains";
    case "uuid":
      return "is";
    case "bool":
      return "isTrue";
    case "number":
      // "at least" is what people actually want from a rating; an exact-match
      // default would make the common case a two-step interaction.
      return field.hint === "rating" ? "gte" : "eq";
    case "date":
      return "onOrAfter";
  }
}

/** `true` for the uuid operators that carry a list rather than a single value. */
export function isUuidListOperator(operator: string): boolean {
  return operator === "in" || operator === "notIn";
}

/**
 * Build a fresh leaf condition with a default value. The shape matches the
 * backend DTOs so the builder never has to translate.
 */
export function newLeaf(field: FieldDef): Condition {
  const op = defaultOperator(field);
  let value: unknown;
  switch (field.operatorType) {
    case "field":
      value = field.enumValues
        ? { operator: op, value: field.enumValues[0]?.value ?? "" }
        : { operator: op, value: "" };
      break;
    case "uuid":
      value = { operator: op, value: "" };
      break;
    case "bool":
      value = { operator: op };
      break;
    case "number":
      value = { operator: op, value: 0 };
      break;
    case "date":
      value = { operator: op, value: new Date().toISOString() };
      break;
  }
  return { [field.key]: value } as Condition;
}

/**
 * Replace the operator/value on a leaf. Operators that take no value
 * (`isNull`, `isNotNull`, `isTrue`, `isFalse`) emit a `{ operator }` shape;
 * `between` emits `{ operator, min?, max? }` (number) or `{ operator,
 * start?, end? }` (date).
 */
export function updateLeafOperator(
  c: Condition,
  field: FieldDef,
  operator: string,
): Condition {
  const key = field.key;
  switch (field.operatorType) {
    case "field": {
      if (operator === "isNull" || operator === "isNotNull") {
        return { [key]: { operator } as FieldOperator } as Condition;
      }
      const prev = (c as Record<string, FieldOperator>)[key];
      const prevValue = "value" in prev ? prev.value : "";
      return {
        [key]: { operator, value: prevValue } as FieldOperator,
      } as Condition;
    }
    case "uuid": {
      const prev = (c as Record<string, UuidOperator>)[key];
      // Carry the selection across a single-value <-> list switch so toggling
      // "is" to "is any of" keeps the library the user already picked.
      const prevValues =
        "values" in prev
          ? prev.values
          : "value" in prev && prev.value
            ? [prev.value]
            : [];
      if (isUuidListOperator(operator)) {
        return {
          [key]: { operator, values: prevValues } as UuidOperator,
        } as Condition;
      }
      return {
        [key]: { operator, value: prevValues[0] ?? "" } as UuidOperator,
      } as Condition;
    }
    case "bool":
      return { [key]: { operator } as { operator: string } } as Condition;
    case "number": {
      const prev = (c as Record<string, NumberOperator>)[key];
      if (operator === "isNull" || operator === "isNotNull") {
        return { [key]: { operator } as NumberOperator } as Condition;
      }
      if (operator === "between") {
        const prevBetween = prev as Extract<
          NumberOperator,
          { operator: "between" }
        >;
        return {
          [key]: {
            operator: "between",
            min: prevBetween?.min ?? null,
            max: prevBetween?.max ?? null,
          } as NumberOperator,
        } as Condition;
      }
      const prevValue = "value" in prev ? (prev as { value: number }).value : 0;
      return {
        [key]: { operator, value: prevValue } as NumberOperator,
      } as Condition;
    }
    case "date": {
      const prev = (c as Record<string, DateOperator>)[key];
      if (operator === "isNull" || operator === "isNotNull") {
        return { [key]: { operator } as DateOperator } as Condition;
      }
      if (operator === "between") {
        const prevBetween = prev as Extract<
          DateOperator,
          { operator: "between" }
        >;
        return {
          [key]: {
            operator: "between",
            start: prevBetween?.start ?? null,
            end: prevBetween?.end ?? null,
          } as DateOperator,
        } as Condition;
      }
      const prevValue =
        "value" in prev
          ? (prev as { value: string }).value
          : new Date().toISOString();
      return {
        [key]: { operator, value: prevValue } as DateOperator,
      } as Condition;
    }
  }
}

export function updateLeafValue<T>(
  c: Condition,
  field: FieldDef,
  patch: Record<string, T>,
): Condition {
  const key = field.key;
  const prev = (c as Record<string, Record<string, unknown>>)[key];
  return {
    [key]: { ...prev, ...patch },
  } as Condition;
}

/**
 * Walk the tree and replace the node at `path`. The path is an array of
 * child indices (from the root). An empty path replaces the root.
 */
export function replaceAtPath(
  root: Condition,
  path: number[],
  next: Condition,
): Condition {
  if (path.length === 0) return next;
  const group = asGroup(root);
  if (!group) return root;
  const [head, ...rest] = path;
  const newChildren = group.children.slice();
  newChildren[head] = replaceAtPath(newChildren[head], rest, next);
  return makeGroup({ mode: group.mode, children: newChildren });
}

export function removeAtPath(root: Condition, path: number[]): Condition {
  if (path.length === 0) {
    // Root removal — collapse to an empty allOf group.
    return makeGroup({ mode: "allOf", children: [] });
  }
  const group = asGroup(root);
  if (!group) return root;
  const [head, ...rest] = path;
  if (rest.length === 0) {
    const newChildren = group.children.filter((_, i) => i !== head);
    return makeGroup({ mode: group.mode, children: newChildren });
  }
  const newChildren = group.children.slice();
  newChildren[head] = removeAtPath(newChildren[head], rest);
  return makeGroup({ mode: group.mode, children: newChildren });
}

/**
 * Reorder the children of the group at `path`, moving the child at `from` to
 * index `to`. Order is cosmetic (`allOf`/`anyOf` are commutative), but it is
 * how the user groups related rows visually, so it round-trips through the
 * stored rule.
 *
 * Returns `root` untouched when the path doesn't resolve to a group or when
 * either index is out of range, so a stray drop event can't corrupt the tree.
 */
export function moveAtPath(
  root: Condition,
  path: number[],
  from: number,
  to: number,
): Condition {
  const group = asGroup(root);
  if (!group) return root;

  if (path.length > 0) {
    const [head, ...rest] = path;
    if (head < 0 || head >= group.children.length) return root;
    const newChildren = group.children.slice();
    newChildren[head] = moveAtPath(newChildren[head], rest, from, to);
    return makeGroup({ mode: group.mode, children: newChildren });
  }

  const count = group.children.length;
  if (from === to) return root;
  if (from < 0 || from >= count || to < 0 || to >= count) return root;

  const newChildren = group.children.slice();
  const [moved] = newChildren.splice(from, 1);
  newChildren.splice(to, 0, moved);
  return makeGroup({ mode: group.mode, children: newChildren });
}

/**
 * Sortable ids are derived from tree position rather than stored on the
 * condition. Builder rows are keyed by index so that editing a value doesn't
 * remount the row and steal focus from the input mid-keystroke, and an id
 * living on the condition would defeat that. Encoding the parent path in the
 * id is also what lets a drop that crossed into another group be rejected.
 */
export function dragId(parentPath: number[], index: number): string {
  return `${parentPath.join(".")}#${index}`;
}

/** The parent-path half of a drag id, back to the index array it encodes. */
export function parsePathKey(key: string): number[] {
  return key === "" ? [] : key.split(".").map(Number);
}

function parseDragId(id: string): { parentKey: string; index: number } | null {
  const sep = id.lastIndexOf("#");
  if (sep === -1) return null;
  const index = Number(id.slice(sep + 1));
  if (!Number.isInteger(index)) return null;
  return { parentKey: id.slice(0, sep), index };
}

/** The parent-path key encoded in a drag id, or `null` if malformed. */
export function dragIdParentKey(id: string): string | null {
  return parseDragId(id)?.parentKey ?? null;
}

/** Full index path to the condition a drag id addresses, or `null` if malformed. */
export function dragIdToPath(id: string): number[] | null {
  const parsed = parseDragId(id);
  if (!parsed) return null;
  const parentPath = parsePathKey(parsed.parentKey);
  if (parentPath.some((n) => !Number.isInteger(n))) return null;
  return [...parentPath, parsed.index];
}

/** Walk `path` from the root, or `null` when it doesn't resolve. */
export function conditionAtPath(
  root: Condition,
  path: number[],
): Condition | null {
  let current = root;
  for (const idx of path) {
    const group = asGroup(current);
    const next = group?.children[idx];
    if (!next) return null;
    current = next;
  }
  return current;
}

/**
 * Resolve a drag from `activeId` onto `overId` into a new tree, or `null` when
 * the drop should be ignored.
 *
 * Rows reorder among their siblings only: moving one in or out of a nested
 * group would change which series the rule matches, and a drag shouldn't
 * change results silently.
 */
export function applyDragMove(
  root: Condition,
  activeId: string,
  overId: string,
): Condition | null {
  const from = parseDragId(activeId);
  const to = parseDragId(overId);
  if (!from || !to) return null;
  if (from.parentKey !== to.parentKey) return null;
  if (from.index === to.index) return null;

  const parentPath = parsePathKey(from.parentKey);
  if (parentPath.some((n) => !Number.isInteger(n))) return null;

  const next = moveAtPath(root, parentPath, from.index, to.index);
  return next === root ? null : next;
}

export function appendChildAtPath(
  root: Condition,
  path: number[],
  child: Condition,
): Condition {
  if (path.length === 0) {
    const group = asGroup(root);
    if (!group) return root;
    return makeGroup({
      mode: group.mode,
      children: [...group.children, child],
    });
  }
  const group = asGroup(root);
  if (!group) return root;
  const [head, ...rest] = path;
  const newChildren = group.children.slice();
  newChildren[head] = appendChildAtPath(newChildren[head], rest, child);
  return makeGroup({ mode: group.mode, children: newChildren });
}

/**
 * Build an empty root condition (an empty `allOf` group). Used when the
 * page mounts with no condition in the URL.
 */
export function emptyRoot(): Condition {
  return makeGroup({ mode: "allOf", children: [] });
}

/**
 * Decide whether a leaf has enough value to send to the API. The backend
 * rejects empty UUIDs (parse error) and treats blank strings / unfilled
 * `between` bounds as invalid. We keep partial leaves in the builder UI so
 * the user can fill them in, but strip them before emitting.
 */
export function isLeafComplete(c: Condition): boolean {
  if (isGroup(c)) return true;
  const key = leafFieldKey(c);
  if (!key) return false;
  const node = (
    c as Record<string, { operator: string } & Record<string, unknown>>
  )[key];
  if (!node || typeof node !== "object") return false;
  const op = node.operator;
  if (
    op === "isNull" ||
    op === "isNotNull" ||
    op === "isTrue" ||
    op === "isFalse"
  ) {
    return true;
  }
  // An empty `in` matches nothing and an empty `notIn` matches everything, so
  // neither is what a user midway through picking libraries meant. Hold the
  // leaf in the UI but keep it out of the request.
  if (isUuidListOperator(op)) {
    const values = (node as { values?: unknown }).values;
    return Array.isArray(values) && values.length > 0;
  }
  if (op === "between") {
    const min = (node as { min?: unknown; start?: unknown }).min;
    const max = (node as { max?: unknown; end?: unknown }).max;
    const start = (node as { start?: unknown }).start;
    const end = (node as { end?: unknown }).end;
    const anyNumber =
      (typeof min === "number" && !Number.isNaN(min)) ||
      (typeof max === "number" && !Number.isNaN(max));
    const anyDate =
      (typeof start === "string" && start.length > 0) ||
      (typeof end === "string" && end.length > 0);
    return anyNumber || anyDate;
  }
  const value = (node as { value?: unknown }).value;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return !Number.isNaN(value);
  return value !== undefined && value !== null;
}

/**
 * Walk a group tree and drop incomplete leaves. Empty groups collapse to
 * `null` so the caller can prune them too. Used by `normalizeForEmit`.
 */
function pruneIncomplete(c: Condition): Condition | null {
  const group = asGroup(c);
  if (!group) return isLeafComplete(c) ? c : null;
  const kept: Condition[] = [];
  for (const child of group.children) {
    const next = pruneIncomplete(child);
    if (next !== null) kept.push(next);
  }
  if (kept.length === 0) return null;
  return makeGroup({ mode: group.mode, children: kept });
}

/**
 * Walk a group tree and drop leaves whose field isn't valid for `target`.
 * The builder lets a user keep a series-only field visible after switching
 * to the Books tab (so they can edit or remove it explicitly), but those
 * leaves must not reach the API — `/books/list` would 422 on
 * `{ titleSort: ... }`. Empty groups collapse to `null`.
 */
function pruneForTarget(c: Condition, target: FieldTarget): Condition | null {
  const group = asGroup(c);
  if (!group) {
    const key = leafFieldKey(c);
    if (!key) return null;
    return findField(target, key) ? c : null;
  }
  const kept: Condition[] = [];
  for (const child of group.children) {
    const next = pruneForTarget(child, target);
    if (next !== null) kept.push(next);
  }
  if (kept.length === 0) return null;
  return makeGroup({ mode: group.mode, children: kept });
}

/**
 * Normalize a root condition for emission to the API:
 *   - When `target` is provided, leaves not valid for that target are
 *     dropped so a series-only filter doesn't 422 the books list.
 *   - Incomplete leaves (empty UUID, empty text, unfilled range bounds) are
 *     dropped so the in-progress UI doesn't trigger 4xx responses.
 *   - An empty group becomes `undefined` (no condition).
 *   - A group with a single leaf child unwraps to the leaf.
 *   - Otherwise the group is passed through.
 *
 * Nested groups stay nested — we don't try to flatten across levels.
 */
export function normalizeForEmit(
  root: Condition,
  target?: FieldTarget,
): Condition | undefined {
  const targetPruned = target ? pruneForTarget(root, target) : root;
  if (targetPruned === null) return undefined;
  const pruned = pruneIncomplete(targetPruned);
  if (pruned === null) return undefined;
  const group = asGroup(pruned);
  if (!group) return pruned;
  if (group.children.length === 0) return undefined;
  if (group.children.length === 1) return group.children[0];
  return pruned;
}

/**
 * Wrap a non-group condition (e.g. a bare leaf from a saved preset) in a
 * root `allOf` group so the builder can edit it uniformly.
 */
export function ensureRoot(c: Condition | undefined): Condition {
  if (!c) return emptyRoot();
  return isGroup(c) ? c : makeGroup({ mode: "allOf", children: [c] });
}

/**
 * Parse a condition written by hand (or pasted from another rule) into a tree
 * the builder can edit.
 *
 * `JSON.parse` alone is not enough: the builder renders a leaf by looking its
 * field up in the catalog and reading a known operator off it, so a
 * syntactically valid but out-of-grammar rule would produce rows that render
 * blank and a request the API rejects with an opaque 422. Every node is
 * checked against the same catalog the builder draws from, and rejections name
 * the offending node so a typo is findable in a large rule.
 *
 * Value checks mirror what the API will accept, not merely what TypeScript
 * types allow: uuids must look like uuids, dates must parse, and a closed enum
 * must hold one of its own values. Empty groups are rejected rather than
 * dropped - `{"allOf": []}` matches the entire library, which is never what
 * someone pasting a rule meant, and silently discarding it would hide the
 * mistake instead of showing it.
 */
export type ParseConditionResult =
  | { ok: true; condition: Condition }
  | { ok: false; error: string };

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Unwound by `parseCondition` into a failed result. */
class ConditionParseError extends Error {}

function fail(path: string, message: string): never {
  throw new ConditionParseError(path ? `At ${path}: ${message}` : message);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isFilledString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function checkLeaf(
  field: FieldDef,
  key: string,
  node: unknown,
  path: string,
): void {
  if (typeof node !== "object" || node === null || Array.isArray(node)) {
    fail(path, `"${key}" must be an object carrying an "operator"`);
  }
  const leaf = node as Record<string, unknown>;
  const operator = leaf.operator;
  if (typeof operator !== "string") {
    fail(path, `"${key}" is missing a string "operator"`);
  }
  if (!operatorsForField(field).includes(operator)) {
    fail(path, `"${key}" does not support operator "${operator}"`);
  }
  // Valueless operators across every family.
  if (
    operator === "isNull" ||
    operator === "isNotNull" ||
    operator === "isTrue" ||
    operator === "isFalse"
  ) {
    return;
  }

  const requireString = (prop: string): string => {
    const value = leaf[prop];
    if (!isFilledString(value)) {
      fail(
        path,
        `"${key}" with operator "${operator}" needs a non-empty string "${prop}"`,
      );
    }
    return value;
  };

  switch (field.operatorType) {
    case "field": {
      const value = requireString("value");
      if (
        field.enumValues &&
        !field.enumValues.some((e) => e.value === value)
      ) {
        const allowed = field.enumValues.map((e) => e.value).join(", ");
        fail(
          path,
          `"${key}" has no value "${value}" (expected one of ${allowed})`,
        );
      }
      return;
    }
    case "uuid": {
      if (isUuidListOperator(operator)) {
        const values = leaf.values;
        if (!Array.isArray(values)) {
          fail(
            path,
            `"${key}" with operator "${operator}" needs an array "values"`,
          );
        }
        if (values.length === 0) {
          fail(
            path,
            `"${key}" with operator "${operator}" needs at least one uuid`,
          );
        }
        for (const value of values) {
          if (typeof value !== "string" || !UUID_PATTERN.test(value)) {
            fail(
              path,
              `"${key}" lists ${JSON.stringify(value)}, which is not a uuid`,
            );
          }
        }
        return;
      }
      const value = requireString("value");
      if (!UUID_PATTERN.test(value)) {
        fail(path, `"${key}" value "${value}" is not a uuid`);
      }
      return;
    }
    case "bool":
      // Every bool operator is valueless and returned above.
      return;
    case "number": {
      if (operator === "between") {
        const { min, max } = leaf;
        if (min != null && !isFiniteNumber(min)) {
          fail(path, `"${key}" has a non-numeric "min"`);
        }
        if (max != null && !isFiniteNumber(max)) {
          fail(path, `"${key}" has a non-numeric "max"`);
        }
        if (!isFiniteNumber(min) && !isFiniteNumber(max)) {
          fail(path, `"${key}" between needs at least one of "min" / "max"`);
        }
        return;
      }
      if (!isFiniteNumber(leaf.value)) {
        fail(
          path,
          `"${key}" with operator "${operator}" needs a number "value"`,
        );
      }
      return;
    }
    case "date": {
      const isDate = (value: unknown): value is string =>
        isFilledString(value) && !Number.isNaN(Date.parse(value));
      if (operator === "between") {
        const { start, end } = leaf;
        if (start != null && !isDate(start)) {
          fail(path, `"${key}" has a "start" that is not an ISO-8601 date`);
        }
        if (end != null && !isDate(end)) {
          fail(path, `"${key}" has an "end" that is not an ISO-8601 date`);
        }
        if (!isDate(start) && !isDate(end)) {
          fail(path, `"${key}" between needs at least one of "start" / "end"`);
        }
        return;
      }
      const value = requireString("value");
      if (!isDate(value)) {
        fail(path, `"${key}" value "${value}" is not an ISO-8601 date`);
      }
      return;
    }
  }
}

function checkNode(node: unknown, target: FieldTarget, path: string): void {
  if (typeof node !== "object" || node === null || Array.isArray(node)) {
    fail(path, "a condition must be a JSON object");
  }
  const keys = Object.keys(node as object);
  if (keys.length !== 1) {
    const found = keys.length === 0 ? "none" : keys.join(", ");
    fail(path, `a condition must have exactly one key, found ${found}`);
  }
  const key = keys[0];
  const value = (node as Record<string, unknown>)[key];

  if (key === "allOf" || key === "anyOf") {
    if (!Array.isArray(value)) {
      fail(path, `"${key}" must be an array of conditions`);
    }
    if (value.length === 0) {
      // An empty group imposes no constraint, so it would quietly match every
      // series in the library.
      fail(path, `"${key}" must contain at least one condition`);
    }
    value.forEach((child, index) => {
      const step = `${key}[${index}]`;
      checkNode(child, target, path ? `${path}.${step}` : step);
    });
    return;
  }

  const field = findField(target, key);
  if (!field) {
    if (findFieldAnyTarget(key)) {
      fail(path, `"${key}" is not available on ${target}`);
    }
    fail(path, `Unknown field "${key}"`);
  }
  checkLeaf(field, key, value, path);
}

export function parseCondition(
  text: string,
  target: FieldTarget,
): ParseConditionResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return {
      ok: false,
      error: `Invalid JSON: ${e instanceof Error ? e.message : String(e)}`,
    };
  }
  try {
    checkNode(parsed, target, "");
  } catch (e) {
    if (e instanceof ConditionParseError)
      return { ok: false, error: e.message };
    throw e;
  }
  return { ok: true, condition: parsed as Condition };
}
